import React, { Suspense, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  FlagProvider,
  useUnleashClient,
  useFlagsStatus
} from '@unleash/proxy-client-react';
import { observer } from 'mobx-react';

import 'tailwindcss/dist/base.min.css';

import { LoadingView } from '@nw/shared/ui';
import {
  addLogEntriesScriptAndInitializeLogEntries,
  initializeSentry,
  sendGAEventToDataLayer
} from '@nw/shared/utils';

import ErrorBoundaryFallback from '../components/ErrorBoundaryFallback';
import {
  unleashConfig,
  UNLEASH_STORAGE_KEY
} from '../constants/UnleashConstants';

import App from './app';

import '../index.css';
import { APP_CONTEXTUAL_LOADING_CONFIG } from '../constants/ContextualLoadingConstants';

const FlagReadyGate: React.FC = () => {
  const { flagsReady, flagsError } = useFlagsStatus();
  const client = useUnleashClient();

  useEffect(() => {
    if (flagsReady && !flagsError) {
      try {
        const toggles = client.getAllToggles();
        localStorage.setItem(UNLEASH_STORAGE_KEY, JSON.stringify(toggles));
      } catch {
        // silently ignore storage write failures
      }
    }
  }, [flagsReady, flagsError, client]);

  useEffect(() => {
    const handleImpression = (event: any) => {
      sendGAEventToDataLayer({
        event: 'unleash_impression',
        feature_name: event.featureName,
        variant: event.variant,
        feature_enabled: event.enabled,
        event_type: event.eventType,
        event_id: event.eventId,
        session_id: event.context?.sessionId,
      });
    };

    client.on('impression', handleImpression);

    return () => {
      client.off('impression', handleImpression);
    };
  }, [client]);

  if (flagsReady || flagsError) {
    return <App />;
  }

  return <LoadingView loadingConfig={APP_CONTEXTUAL_LOADING_CONFIG} />;
};

declare global {
  interface Window {
    dataLayer: any;
    analytics: any;
    mixpanel: any;
    R7Insight: any;
  }
}

@observer
class EnhancedApp extends React.Component {
  constructor(props) {
    super(props);
    initializeSentry();
    window.addEventListener('load', this.onPageLoad);
  }

  componentWillUnmount() {
    window.removeEventListener('load', () => null);
  }

  onPageLoad = (): void => {
    addLogEntriesScriptAndInitializeLogEntries();
  };

  render() {
    return (
      <Sentry.ErrorBoundary fallback={ErrorBoundaryFallback}>
        <Suspense fallback={<LoadingView loadingConfig={APP_CONTEXTUAL_LOADING_CONFIG} /> as any}>
          <FlagProvider config={unleashConfig}>
            <FlagReadyGate />
          </FlagProvider>
        </Suspense>
      </Sentry.ErrorBoundary>
    );
  }
}

export default EnhancedApp;
